import Component from "@glimmer/component";
import { service } from "@ember/service";
import { on } from "@ember/modifier";
import { fn } from "@ember/helper";
import { pageTitle } from "ember-page-title";
import type RouterService from "@ember/routing/router-service";
import type SequencerService from "#services/sequencer.ts";
import { MIN_BPM, MAX_BPM } from "#services/sequencer.ts";
import { Song } from "#models/song.ts";
import { Channel } from "#models/channel.ts";
import { Step } from "#models/step.ts";
import { Sound } from "#models/sound.ts";
import ChannelRow from "#components/channel-row.gts";
import PlayerControls from "#components/player-controls.gts";
import PresetSelector from "#components/preset-selector.gts";
import AddCircleIcon from "~icons/mdi/plus-circle";

export interface PlayerSignature {
  Args: {
    /** For all mutations to the song data, we notify our container. */
    onSongUpdated?: () => void;
  };
}

/**
 * The Player is the user-facing controls for the sequencer service.
 */
export default class PlayerComponent extends Component<PlayerSignature> {
  @service declare sequencer: SequencerService;
  @service declare router: RouterService;

  get song(): Song {
    return this.sequencer.song!;
  }

  get isPlaying(): boolean {
    return this.sequencer.isPlaying;
  }

  togglePlay = (): void => {
    this.sequencer.togglePlay();
  };

  stop = (): void => {
    this.sequencer.stop();
  };

  decreaseTempo = (): void => {
    this.song.tempo = Math.max(MIN_BPM, this.song.tempo - 5);
    this.args.onSongUpdated?.();
  };

  increaseTempo = (): void => {
    this.song.tempo = Math.min(MAX_BPM, this.song.tempo + 5);
    this.args.onSongUpdated?.();
  };

  previewSound = (sound: Sound): void => {
    this.sequencer.previewSound(sound);
  };

  nextSound = (channel: Channel): void => {
    channel.sound = Sound.next(channel.sound);
    this.args.onSongUpdated?.();
  };

  previousSound = (channel: Channel): void => {
    channel.sound = Sound.previous(channel.sound);
    this.args.onSongUpdated?.();
  };

  toggleMute = (channel: Channel): void => {
    channel.isMuted = !channel.isMuted;
    this.args.onSongUpdated?.();
  };

  handleStepClick = (step: Step): void => {
    step.cycleVelocity();
    this.args.onSongUpdated?.();
  };

  addStep = (channel: Channel): void => {
    channel.addStep();
    this.args.onSongUpdated?.();
  };

  removeStep = (channel: Channel): void => {
    channel.removeStep();
    this.args.onSongUpdated?.();
  };

  addChannel = (): void => {
    const existingSoundIds = this.song.channels.map((c) => c.sound.id);
    const nextSound =
      Sound.all.find((s) => !existingSoundIds.includes(s.id)) ??
      Sound.next(this.song.channels[this.song.channels.length - 1]?.sound);

    const newChannel = new Channel({
      sound: nextSound,
      volume: 1.0,
    });
    this.song.channels.push(newChannel);
    this.args.onSongUpdated?.();
  };

  removeChannel = (channel: Channel): void => {
    this.song.removeChannel(channel);
    this.args.onSongUpdated?.();
  };

  handleSelectPreset = (song: Song): void => {
    this.sequencer.loadSong(song);
    this.args.onSongUpdated?.();
  };

  willDestroy(): void {
    this.sequencer.stop();
    super.willDestroy();
  }

  <template>
    {{pageTitle "Play"}}

    <div data-test-play-page>
      <div class="mx-auto space-y-6">

        {{! Transport Controls & Preset Selector }}
        <header class="flex flex-wrap items-center justify-between gap-4 p-3 bg-slate-800 rounded">
          <PlayerControls
            @isPlaying={{this.isPlaying}}
            @tempo={{this.song.tempo}}
            @tick={{this.sequencer.currentTick}}
            @onTogglePlay={{this.togglePlay}}
            @onStop={{this.stop}}
            @onDecreaseTempo={{this.decreaseTempo}}
            @onIncreaseTempo={{this.increaseTempo}}
          />
          <PresetSelector @onSelect={{this.handleSelectPreset}} />
        </header>

        {{! Main Sequencer Matrix }}
        <main class="space-y-3" data-test-channel-matrix>
          {{#each this.song.channels as |channel|}}
            <ChannelRow
              @channel={{channel}}
              @tick={{this.sequencer.currentTick}}
              @onPreviewSound={{this.previewSound}}
              @onNextSound={{fn this.nextSound channel}}
              @onPreviousSound={{fn this.previousSound channel}}
              @onToggleMute={{fn this.toggleMute channel}}
              @onDeleteChannel={{fn this.removeChannel channel}}
              @onStepClick={{this.handleStepClick}}
              @onAddStep={{fn this.addStep channel}}
              @onRemoveStep={{fn this.removeStep channel}}
            />
          {{else}}
            <div class="text-center py-12 text-slate-500 text-sm">
              No channels yet. Click "+ Add Channel" below to start making beats.
            </div>
          {{/each}}

          {{! Add Channel Button }}
          <div class="pt-2">
            <button
              type="button"
              class="w-full py-3 border-2 border-dashed border-slate-800 hover:border-slate-600 hover:bg-slate-900/50 rounded text-slate-400 hover:text-white font-semibold text-sm transition-all duration-150 cursor-pointer flex items-center justify-center gap-2"
              data-test-add-channel-button
              {{on "click" this.addChannel}}
            >
              <AddCircleIcon class="w-5 h-5" />
              <span>Add Channel</span>
            </button>
          </div>
        </main>

      </div>
    </div>
  </template>
}
